import React, { useEffect, useState } from "react";
import ToolShell from "@/components/ToolShell";
import CollegeSelect from "@/components/CollegeSelect";
import ResidencyToggle from "@/components/ResidencyToggle";
import Spinner from "@/components/Spinner";
import { track } from "@/lib/analytics";
import { FileSearch, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DecoderPage() {
  const [school, setSchool] = useState("");
  const [residency, setResidency] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { track.tool_opened("decoder"); }, []);

  const residencyMissing = !!school.trim() && !residency;
  const canSubmit = !!text.trim() && !residencyMissing;

  const decode = async () => {
    if (!canSubmit) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const res = await fetch(`${API}/decode`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          raw_text: text,
          school_name: school || undefined,
          residency: school && residency ? residency : undefined,
        }),
      });
      if (!res.ok) throw new Error("request-failed");
      setResult(await res.json());
      track.decoder_submitted();
    } catch {
      setErr("Couldn't decode that right now. Try again in a moment.");
    } finally { setBusy(false); }
  };

  return (
    <ToolShell tool="Tool 04 · Aid Letter Decoder" title="Paste the letter. Get plain English back." subtitle="Paste the whole aid letter, table, email, whatever they sent you. We extract the numbers, explain each line, and write one question for you to email the aid office. Nothing is stored.">
      <div className="card" data-testid="decoder-form">
        <label className="block text-sm text-ink-muted mb-2">School name (optional)</label>
        <div className="mb-4">
          <CollegeSelect
            value={school}
            onChange={setSchool}
            placeholder="Vanderbilt University"
            inputClassName="input"
            testId="decoder-school"
          />
        </div>
        {!!school.trim() && (
          <div className="mb-4">
            <ResidencyToggle
              value={residency}
              onChange={setResidency}
              testId="decoder-residency"
            />
          </div>
        )}
        <label className="block text-sm text-ink-muted mb-2">Paste the aid letter</label>
        <textarea className="input" rows={10} value={text} onChange={e => setText(e.target.value)} placeholder="Paste the email or letter text here…" data-testid="decoder-text" />
        <div className="mt-4 flex gap-3">
          <button onClick={decode} disabled={busy || !canSubmit} className="btn btn-primary" data-testid="decoder-submit">
            <FileSearch className="w-4 h-4" /> {busy ? "Decoding…" : "Decode this letter"}
          </button>
        </div>
        {residencyMissing && (
          <div className="mt-2 text-xs text-amber" data-testid="decoder-residency-required">
            Please pick In-State or Out-of-State for {school}.
          </div>
        )}
        {err && <div className="mt-3 text-sm text-danger" data-testid="decoder-err">{err}</div>}
      </div>

      {busy && !result && (
        <Spinner variant="block" label="Reading the letter and translating each line." testId="decoder-spinner" />
      )}

      {result && (
        <section className="mt-8 space-y-5" data-testid="decoder-result">
          <div className="card">
            <div className="text-xs uppercase tracking-widest text-ink-dim mb-2">TLDR</div>
            <p className="text-lg text-ink leading-relaxed">{result.tldr}</p>
          </div>
          {result.breakdown?.length > 0 && (
            <div className="card">
              <div className="text-xs uppercase tracking-widest text-ink-dim mb-3">Breakdown</div>
              <table className="w-full text-sm">
                <tbody>
                  {result.breakdown.map((row, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="py-3 pr-4 text-ink">{row.label}</td>
                      <td className="py-3 pr-4 text-xs uppercase tracking-widest text-ink-dim">{row.kind}</td>
                      <td className="py-3 text-right font-heading text-xl text-ink">${Number(row.amount || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {result.know?.length > 0 && (
            <div className="card">
              <div className="text-xs uppercase tracking-widest text-ink-dim mb-3">Know</div>
              <ul className="space-y-2 text-sm text-ink-muted list-disc pl-5">
                {result.know.map((k, i) => <li key={i}>{k}</li>)}
              </ul>
            </div>
          )}
          {result.question && (
            <div className="card">
              <div className="text-xs uppercase tracking-widest text-ink-dim mb-2">Ask the aid office</div>
              <p className="text-ink italic">"{result.question}"</p>
            </div>
          )}
          <div className="card border-border flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-success mt-0.5" />
            <p className="text-xs text-ink-muted">Nothing you pasted was saved. The decoded result lives only in this browser tab.</p>
          </div>
        </section>
      )}
    </ToolShell>
  );
}
